import { getUnitLabel, type PricingType } from "@/hooks/usePricingTypes";

/**
 * Packages attached to a published menu (#932), shown as a "Packages" section
 * on /menu/<slug> and as options in the quote form's Package select.
 */

export interface MenuPackageSlot {
  quantity: number;
  sort_order: number;
  label_override: string | null;
  category: { name: string } | null;
}

export interface MenuPackageItem {
  quantity: number;
  menu_item: { name: string } | null;
}

export interface MenuPackage {
  id: string;
  name: string;
  price: number;
  pricing_type: string;
  min_guests: number | null;
  show_prices: boolean;
  is_published: boolean;
  is_active: boolean;
  category_slots: MenuPackageSlot[] | null;
  items: MenuPackageItem[] | null;
}

/** One `published_menu_packages` row with its package embedded. */
export interface MenuPackageLinkRow {
  sort_order: number;
  // Null when RLS hides the package (unpublished) from the caller.
  package: MenuPackage | null;
}

export const MENU_PACKAGES_SELECT =
  "sort_order, package:packages(id, name, price, pricing_type, min_guests, show_prices, is_published, is_active, category_slots:package_category_slots(quantity, sort_order, label_override, category:menu_categories(name)), items:package_items(quantity, menu_item:menu_items(name)))";

/**
 * Link rows → the packages to show, in link order. Drops packages the caller
 * can't see and ones that aren't published + active, so staff previewing the
 * page see the same list as the public.
 */
export function toMenuPackages(rows: MenuPackageLinkRow[] | null | undefined): MenuPackage[] {
  return [...(rows ?? [])]
    .sort((a, b) => a.sort_order - b.sort_order)
    .flatMap((row) => {
      const pkg = row.package;
      if (!pkg || !pkg.is_published || !pkg.is_active) return [];
      const slots = [...(pkg.category_slots ?? [])].sort((a, b) => a.sort_order - b.sort_order);
      return [{ ...pkg, category_slots: slots }];
    });
}

/** "Mains (choose 2) · Sides (choose 2) · Cookie" — slots first, then fixed items. */
export function packageSummary(pkg: MenuPackage): string {
  const slots = (pkg.category_slots ?? []).map((s) => {
    const label = s.label_override || s.category?.name || "Items";
    return `${label.charAt(0).toUpperCase()}${label.slice(1)} (choose ${s.quantity})`;
  });
  const items = (pkg.items ?? []).flatMap((i) => {
    const name = i.menu_item?.name;
    if (!name) return [];
    return [i.quantity > 1 ? `${i.quantity} × ${name}` : name];
  });
  return [...slots, ...items].join(" · ");
}

/** Price and unit for display, or `null` when either the menu or the package hides prices. */
export function packagePriceText(
  pkg: MenuPackage,
  menuShowPrices: boolean,
  pricingTypes: PricingType[] | undefined,
): { price: string; unit: string | null } | null {
  if (!menuShowPrices || !pkg.show_prices) return null;
  return {
    price: `$${pkg.price.toFixed(2)}`,
    unit: getUnitLabel(pricingTypes, pkg.pricing_type) || null,
  };
}

/** "The Seated ($65.00 / person)", or just the name when prices are hidden. */
export function packageOptionLabel(
  pkg: MenuPackage,
  menuShowPrices: boolean,
  pricingTypes: PricingType[] | undefined,
): string {
  const price = packagePriceText(pkg, menuShowPrices, pricingTypes);
  if (!price) return pkg.name;
  return `${pkg.name} (${price.price}${price.unit ? ` / ${price.unit}` : ""})`;
}

/**
 * Some packages are stored in ALL CAPS ("THE SEATED"), which reads oddly in a
 * sentence. Title-case those; names with any lowercase letter are kept as stored.
 */
export function packageDisplayName(name: string): string {
  if (/\p{Ll}/u.test(name)) return name;
  return name.toLowerCase().replace(/(^|[\s\-/(&])(\p{L})/gu, (_, sep: string, ch: string) => sep + ch.toUpperCase());
}
